<!--
  ClientPeoplePanel.vue -- read-only list of Accounts attached to the
  Client.

  An Account is "attached" via Account.client_id (Client Admin / Store
  Manager tiers). The panel surfaces these so the Distributor knows
  who has access on the Client side. No add/edit/remove for MVP --
  the existing /distributor/account/team surface owns those flows; this
  is a glance view.

  Reads GET /api/clients/<id>/people -- a dedicated, tenant-scoped route,
  because ``accounts.phone`` is deliberately absent from the generic Account
  API. Each row: name (the email when both name parts are blank), role
  (``role_slug``, the stable key), email, phone ("--" when blank), and the
  primary contact marked (the server's ``is_primary``). The clients list's
  "N people" count is the same set.
-->
<template>
    <div class="cpp" data-testid="client-people-panel">
        <div v-if="loading" class="cpp__loading">Loading people...</div>
        <div v-else-if="error" class="cpp__error">{{ error }}</div>
        <p v-else-if="people.length === 0" class="cpp__empty">
            No one on the client side has signed up yet.
        </p>
        <ul v-else class="cpp__list">
            <li
                v-for="p in people"
                :key="p.id"
                class="cpp__item"
                :data-testid="`client-people-row-${p.id}`"
            >
                <span class="cpp__name">
                    <span :data-testid="`client-people-name-${p.id}`">{{
                        displayName(p)
                    }}</span>
                    <span
                        v-if="p.is_primary"
                        class="cpp__primary"
                        :data-testid="`client-people-primary-${p.id}`"
                    >
                        Primary contact
                    </span>
                </span>
                <span
                    v-if="roleLabel(p.role_slug)"
                    class="cpp__role"
                    :data-testid="`client-people-role-${p.id}`"
                >
                    {{ roleLabel(p.role_slug) }}
                </span>
                <span
                    v-if="hasName(p)"
                    class="cpp__email"
                    :data-testid="`client-people-email-${p.id}`"
                    >{{ p.email }}</span
                >
                <span
                    class="cpp__phone"
                    :data-testid="`client-people-phone-${p.id}`"
                    >{{ p.phone || '--' }}</span
                >
            </li>
        </ul>
    </div>
</template>

<script>
import api from '@/services/api'

// Display names of the roles a person can hold inside a client (the platform
// role names; ``role_slug`` is the stable key the Account serializes). An
// unlisted slug is title-cased rather than hidden.
const ROLE_LABELS = {
    client_admin: 'Client Admin',
    store_manager: 'Store Manager',
}

export default {
    name: 'ClientPeoplePanel',
    props: {
        clientId: { type: [Number, String], required: true },
    },
    data() {
        return {
            people: [],
            loading: true,
            error: '',
        }
    },
    watch: {
        clientId() {
            this.load()
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        roleLabel(slug) {
            if (!slug) return ''
            return (
                ROLE_LABELS[slug] ||
                String(slug)
                    .split('_')
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ')
            )
        },
        fullName(p) {
            return [p.first_name, p.last_name]
                .map((n) => (n || '').trim())
                .filter(Boolean)
                .join(' ')
        },
        // A person with no name yet is shown by email (never a blank row);
        // the email is then not repeated beside it.
        hasName(p) {
            return this.fullName(p) !== ''
        },
        displayName(p) {
            return this.fullName(p) || p.email || ''
        },
        async load() {
            this.loading = true
            this.error = ''
            try {
                const res = await api.get(`/clients/${this.clientId}/people`)
                const rows = res?.data?.people
                this.people = Array.isArray(rows) ? rows : []
            } catch (e) {
                // The people route needs client.read. A 403 renders as
                // empty rather than an error -- the caller isn't allowed
                // to see who's on the client side, which is a legitimate
                // outcome, not a bug.
                if (e?.response?.status === 403) {
                    this.people = []
                    this.error = ''
                } else {
                    this.error = 'Could not load people.'
                    this.people = []
                }
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.cpp__loading,
.cpp__error,
.cpp__empty {
    padding: 12px;
    color: var(--tf-fg-2);
    font-size: 13px;
}
.cpp__error {
    color: #c0392b;
}
.cpp__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.cpp__item {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 12px;
    padding: 8px 12px;
    background: var(--tf-surface-alt, #f7f8fa);
    border-radius: var(--tf-radius-xs, 4px);
    font-size: 13px;
}
.cpp__name {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 8px;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.cpp__email,
.cpp__phone {
    color: var(--tf-fg-3, #6b7280);
    overflow-wrap: break-word;
}
.cpp__role {
    padding: 2px 8px;
    border-radius: 10px;
    background: var(--tf-surface, #eef0f4);
    color: var(--tf-fg-2);
    font-size: 12px;
}
.cpp__primary {
    font-size: 12px;
    font-weight: 400;
    color: var(--tf-accent, #2c5fff);
}
</style>
